import { otto } from "../../../exports/nexus.js";
import type {
  NavigationScope,
  NavigationScopeName,
  NavigationScopeOptions,
} from "./scope.types.js";
import { NAV_SCOPES } from "../lib/utils.js";
import { getCurrentNavigation } from "../lib/navigation.client.js";
import { sanitizeState } from "../state/state.utils.js";
import { defineContext, historyPatched } from "../lib/history.client.js";
import { navigate } from "../lib/navigate.client.js";
import { hashFiltered, pathFiltered, scopeCache, scopesFiltered, searchFiltered } from "./cache.js";
import { paramTypes } from "../params/params.utils.js";
import {
  ensureEntry,
  ensureRegExpEntry,
  getScopedState,
  initializeNamespaceWithCurrentNavigation,
  setScope,
} from "./utils.js";
import { baseLocation } from "../lib/location.js";
import { scopeScope as log } from "./scope.logger.js";

export const scope = <N extends NavigationScopeName, O extends NavigationScopeOptions>(
  name: N,
  options: O = {} as O,
): NavigationScope<N, O> => {
  /*                                                                                       */
  const { hash: hashFilters = [], search: searchFilters = [] } = options;
  const pathPattern = options.path ? new RegExp(options.path) : undefined;

  const scopeId = `${name}:${hashFilters.join("|")}:${searchFilters.join("|")}:${pathPattern?.toString()}`;
  log.info("Aquiring scope", scopeId, "with options", options);

  /*                                              */
  if (scopeCache.has(scopeId)) return scopeCache.get(scopeId) as NavigationScope<N, O>;

  /*                                              */
  const namespace = otto.navigation[NAV_SCOPES][scopeId];

  /*                                                         */
  const [, state, hash, search] = getCurrentNavigation(undefined, hashFilters, searchFilters);
  const scopeState = getScopedState(name, state);
  initializeNamespaceWithCurrentNavigation(
    namespace,
    scopeState.clone(),
    hash.clone(),
    search.clone(),
  );

  /*                     */
  const res: NavigationScope<string, NavigationScopeOptions> = {
    name,
    key: scopeId,
    current: namespace.current,
    currentHash: namespace.currentHash,
    currentSearch: namespace.currentSearch,
    currentState: namespace.currentState,
    onNavigate: namespace.onNavigate,

    hash(params) {
      if (params) hash.fromObject(params);
      return hash as never;
    },

    search(params) {
      if (params) search.fromObject(params);
      return search as never;
    },

    state(params) {
      if (params) scopeState.fromObject(params as never);
      return scopeState as never;
    },

    async navigate(urlOrString = "", o = {}) {
      const { history: navigationType = "push" } = o;
      log.debug("Navigation request:", navigationType, "for:", urlOrString, "with options", o);

      /*                                                               */
      if (o.state) scopeState.fromObject(o.state);

      /*                                                      */
      /*                                               */
      const nextUrl = new URL(urlOrString, baseLocation.href);

      /*                       */
      for (const [type, source] of [
        [paramTypes[0], search],
        [paramTypes[1], hash],
      ] as const) {
        /*                                                  */
        /*                                                       */
        if (nextUrl[type]) source.fromString(nextUrl[type]);

        /*                                                          */
        /*                                                                       */
        /*                                                                    */
        nextUrl[type] = source.toString(baseLocation[type]);
      }

      /*                            */
      const stateManaged = sanitizeState(o.clear ? {} : (await historyPatched()).state, true);
      setScope(name, stateManaged, scopeState.toObject());

      /*                                                                     */
      /*                                                                                           */
      log.info("Navigate to", nextUrl.href, "as", navigationType, "with state", stateManaged);
      return navigate(nextUrl, { ...o, scope: name, state: stateManaged });
    },

    push: (o, url) => res.navigate(url, { ...o, history: "push" }),
    replace: (o, url) => res.navigate(url, { ...o, history: "replace" }),

    back: async (o) =>
      (await historyPatched()).back.call(defineContext({ ...o, scope: name })) ?? true,
    forward: async (o) =>
      (await historyPatched()).forward.call(defineContext({ ...o, scope: name })) ?? true,

    reload: () => location.reload() ?? true,
    go: async (delta, o) =>
      (await historyPatched()).go.call(defineContext({ ...o, scope: name }), delta) ?? true,
  };

  /*                                           */
  scopeCache.set(scopeId, res);

  /*                                      */
  ensureEntry(scopesFiltered, name).add(res);
  ensureRegExpEntry(pathFiltered, pathPattern)?.add(res);
  hashFilters.forEach((ho) => ensureEntry(hashFiltered, ho).add(res));
  searchFilters.forEach((so) => ensureEntry(searchFiltered, so).add(res));

  return res as NavigationScope<N, O>;
};
